// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Cross-org switcher for QH/Quantino operators. Operators have no patients of
// their own — they "act as" a client org. Selecting one sets the act-as context
// (sent as X-Org-Context on /v1/* calls; backend re-scopes + audits). There is
// NO default selection by design: an operator sees empty data until they pick,
// and a loud banner (ActingAsBanner) shows the active org. Hidden for client staff.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Building2, ChevronDown } from 'lucide-react'
import { useAuth } from '@/context/auth-context'
import { getOperatorOrgs } from '@/lib/api'
import type { OrgOption } from '@/types'

export default function OrgSwitcher() {
  const { t } = useTranslation()
  const { isOperator, actingAsOrg, setActingAsOrg } = useAuth()
  const [orgs, setOrgs] = useState<OrgOption[]>([])
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!isOperator) return
    let active = true
    getOperatorOrgs()
      .then((list) => {
        if (!active) return
        setOrgs(list)
        // Auto-select when there's exactly one org — unambiguous, removes the
        // friction, and the "Operating as" banner still shows it. With multiple
        // orgs we require an explicit pick (the act-as guardrail).
        if (!actingAsOrg && list.length === 1 && list[0]) setActingAsOrg(list[0])
      })
      .catch(() => active && setError(true))
    return () => {
      active = false
    }
    // actingAsOrg/setActingAsOrg intentionally omitted — fetch+auto-select runs
    // once when the operator is identified; selection changes don't re-fetch.
  }, [isOperator])

  if (!isOperator || error || orgs.length === 0) return null

  return (
    <label className="flex items-center gap-2 rounded-lg border border-border bg-secondary/60 pl-2.5 pr-1 h-8">
      <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden />
      <span className="sr-only">{t('orgSwitcher.label')}</span>
      <div className="relative flex items-center">
        <select
          value={actingAsOrg?.id ?? ''}
          onChange={(e) => {
            const next = orgs.find((o) => o.id === e.target.value) ?? null
            setActingAsOrg(next)
          }}
          aria-label={t('orgSwitcher.label')}
          className="appearance-none bg-transparent text-sm text-foreground font-medium pr-5 focus:outline-none cursor-pointer max-w-[12rem] truncate"
        >
          <option value="">{t('orgSwitcher.placeholder')}</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.displayName}
            </option>
          ))}
        </select>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground absolute right-0 pointer-events-none" aria-hidden />
      </div>
    </label>
  )
}
